import * as React from "react"
import { Check, X, ChevronsUpDown, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/**
 * A premium searchable multi-select component inspired by modern CRM designs.
 * @param {Object} props
 * @param {Array<{label: string, value: any}>} props.options - Array of options to select from.
 * @param {Array<any>} props.selected - Array of currently selected values.
 * @param {Function} props.onChange - Callback function when selection changes.
 * @param {string} props.placeholder - Placeholder text when nothing is selected.
 * @param {string} props.emptyMessage - Message to show when no results are found.
 * @param {boolean} props.single - If true, only one item can be selected.
 * @param {string} props.className - Optional className for the trigger.
 */
export function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select options...",
  emptyMessage = "No results found.",
  single = false,
  className,
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [triggerWidth, setTriggerWidth] = React.useState(0)
  const triggerRef = React.useRef(null)
  const inputRef = React.useRef(null)

  React.useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth)
    }
  }, [open])

  const handleUnselect = (itemValue) => {
    window.requestAnimationFrame(() => {
      onChange(selected.filter((i) => i !== itemValue))
    })
  }

  const handleSelect = (itemValue) => {
    window.requestAnimationFrame(() => {
      if (single) {
        onChange(selected[0] === itemValue ? [] : [itemValue])
        setOpen(false)
      } else {
        if (selected.includes(itemValue)) {
          handleUnselect(itemValue)
        } else {
          onChange([...selected, itemValue])
        }
      }
    })
  }

  const handleClearAll = (e) => {
    e.preventDefault()
    e.stopPropagation()
    window.requestAnimationFrame(() => {
      onChange([])
    })
  }

  // Filter options based on the input value for the trigger area if needed
  // But we primarily use the Command component for searching.

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex min-h-[42px] h-auto w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-all hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary cursor-text",
            open && "ring-2 ring-primary/20 border-primary",
            className
          )}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex flex-wrap gap-1.5 items-center">
            {selected.length > 0 ? (
              selected.map((itemValue) => {
                const option = options.find((o) => o.value === itemValue)
                return (
                  <Badge
                    key={itemValue}
                    variant="secondary"
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary/20 text-primary border-primary/30 gap-1 hover:bg-primary/30 transition-colors",
                      single && "px-3 py-1 text-sm"
                    )}
                  >
                    {option?.label || itemValue}
                    {!single && (
                      <button
                        type="button"
                        className="rounded-full outline-none focus:ring-1 focus:ring-primary"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleUnselect(itemValue)
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleUnselect(itemValue)
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                )
              })
            ) : (
              single ? (
                <span className="text-muted-foreground py-0.5">
                  {placeholder}
                </span>
              ) : (
                <></>
              )
            )}
            {!single && (
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => setOpen(true)}
                placeholder={selected.length === 0 ? placeholder : ""}
                className="ml-1 flex-1 bg-transparent outline-none placeholder:text-muted-foreground text-foreground min-w-[80px]"
              />
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0 pl-2">
            {selected.length > 0 && (
              <X
                className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                onClick={handleClearAll}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border-border rounded-xl overflow-hidden"
        align="start"
        style={{ width: triggerWidth > 0 ? triggerWidth : "auto", minWidth: "240px" }}
      >
        <Command className="border-none w-full" filter={(value, search) => {
          const option = options.find(o => String(o.value) === value);
          if (option?.label.toLowerCase().includes(search.toLowerCase())) return 1;
          return 0;
        }}>
          <div className="flex items-center border-b px-3 bg-muted/30">
            <CommandInput
              placeholder="Search options..."
              className=" border-none focus:ring-0"
              value={inputValue}
              onValueChange={setInputValue}
            />
          </div>
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-6 text-sm text-center text-muted-foreground">
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup className="p-1 mb-1">
              {options.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <CommandItem
                    key={String(option.value)}
                    value={String(option.value)}
                    onSelect={() => handleSelect(option.value)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5  cursor-pointer transition-colors mb-1",
                      isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    )}
                  >
                    <span className={cn("font-medium", isSelected ? "text-primary-foreground" : "text-foreground")}>
                      {option.label}
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-primary-foreground" />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {selected.length} selected
              </span>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs text-destructive hover:text-destructive/80 font-semibold transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
            >
              Done
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
